/* useAdminChromeMode — shared read of the admin's dark/light chrome mode.

   AdminLayout owns the toggle button + localStorage write; any other admin
   surface that needs to know the mode (so it can skip applying the tenant's
   dark palette onto <html> in light mode, e.g. ThemeSection's live preview)
   reads it through this hook so the two stay in sync without prop drilling.

   Updates fire on:
     - the `lumetier:admin-chrome-changed` custom event (dispatched by the
       toggle in AdminLayout when the user flips dark ↔ light), and
     - the browser `storage` event (so multiple tabs stay aligned).
*/
import { useEffect, useState } from "react";

export const ADMIN_CHROME_EVENT = "lumetier:admin-chrome-changed";

function readMode() {
  if (typeof window === "undefined") return true; // SSR default dark
  try {
    const v = localStorage.getItem("admin_theme");
    return v === null ? true : v === "dark";
  } catch {
    return true;
  }
}

export function useAdminChromeMode() {
  const [dark, setDark] = useState(readMode);

  useEffect(() => {
    function refresh() { setDark(readMode()); }
    window.addEventListener(ADMIN_CHROME_EVENT, refresh);
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener(ADMIN_CHROME_EVENT, refresh);
      window.removeEventListener("storage", refresh);
    };
  }, []);

  return dark;
}
